/**
 * Shared `<select>`: form token styling, chevron icon, `state` like `Input`.
 *
 * Usage:
 * - Pass `options={[{ value, label }, ...]}` — no manual `<option>` elements.
 * - Or use `children` with `<option>` elements (legacy API).
 * - `selectSize`: `sm` | `md` | `lg` (same height as `Input` of the same size).
 * - `hidePlaceholder`: omit the leading empty option (when handling default value yourself).
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../cn';
import { getFieldBaseStyle, getFieldColors } from './fieldStyles';
const heightBySize = {
    sm: 'var(--field-height-sm, 32px)',
    md: 'var(--field-height-md, 40px)',
    lg: 'var(--field-height-lg, 48px)',
};
const paddingXBySize = {
    sm: 'var(--field-padding-x, 12px)',
    md: 'var(--field-padding-x, 12px)',
    lg: 'var(--field-padding-x, 12px)',
};
export const Select = ({ state = 'default', className, disabled, readOnly, onFocus, onBlur, onChange, value, placeholder = 'Select...', options, selectSize = 'md', hidePlaceholder = false, children, style: selectElementStyle, ...props }) => {
    const effectiveState = disabled ? 'disabled' : readOnly ? 'readonly' : state;
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const focusColors = getFieldColors(effectiveState);
    const borderWidth = effectiveState === 'error' || effectiveState === 'success' || focused
        ? 'var(--stroke-active, 1.5px)'
        : 'var(--stroke-normal, 1px)';
    const borderColor = focused
        ? focusColors.border
        : hovered
            ? 'var(--form-border-hover, rgba(11,18,32,0.36))'
            : 'var(--form-border, rgba(11,18,32,0.22))';
    const ringShadow = focused && effectiveState !== 'readonly' && effectiveState !== 'disabled'
        ? `0 0 0 3px ${focusColors.ring}`
        : 'none';
    const wrapperStyle = {
        ...getFieldBaseStyle(effectiveState),
        borderStyle: 'solid',
        borderWidth,
        borderColor,
        borderRadius: 'var(--radius-md, 8px)',
        boxShadow: ringShadow,
        height: heightBySize[selectSize],
        opacity: effectiveState === 'disabled' ? 0.6 : 1,
        transition: 'all 150ms ease',
    };
    const isPlaceholderSelected = value == null || value === '' || (Array.isArray(value) && value.length === 0);
    const optionNodes = useMemo(() => {
        if (!(options === null || options === void 0 ? void 0 : options.length))
            return children;
        return options.map((opt) => (React.createElement("option", { key: opt.value, value: opt.value, disabled: opt.disabled }, opt.label)));
    }, [children, options]);
    const showPlaceholderRow = !hidePlaceholder && placeholder != null && placeholder !== '';
    const padX = paddingXBySize[selectSize];
    const chevronClass = selectSize === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
    const rightPad = selectSize === 'lg' ? 'pr-11' : 'pr-10';
    return (React.createElement("div", { className: cn('relative w-full', className), style: wrapperStyle, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
        React.createElement("select", { ...props, value: value, disabled: effectiveState === 'disabled' ? true : undefined, onFocus: (e) => {
                setFocused(true);
                onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
            }, onBlur: (e) => {
                setFocused(false);
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
            }, onChange: (e) => {
                if (effectiveState === 'readonly')
                    return;
                onChange === null || onChange === void 0 ? void 0 : onChange(e);
            }, className: cn('h-full w-full appearance-none bg-transparent text-[length:var(--text-body,14px)] outline-none', rightPad, isPlaceholderSelected
                ? 'text-[color:var(--form-placeholder,rgba(11,18,32,0.38))]'
                : 'text-[color:var(--form-fg,#0b1220)]'), style: {
                paddingLeft: padX,
                ...(selectElementStyle && typeof selectElementStyle === 'object' ? selectElementStyle : {}),
            } },
            showPlaceholderRow ? (React.createElement("option", { value: "", disabled: true, hidden: true }, placeholder)) : null,
            optionNodes),
        React.createElement("div", { className: "pointer-events-none absolute inset-y-0 right-3 flex items-center text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]" },
            React.createElement(ChevronDown, { className: chevronClass, "aria-hidden": true }))));
};
export default Select;
